"use client"

import * as React from "react"
import { Bar, BarChart, CartesianGrid, XAxis } from "recharts"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart"
import { OrdenespagoService } from "../../services/ordenespagoService"
import { ordenPagoEstados, getOrdenPagoColor, getOrdenPagoColorValue } from "../../styles/tokens/colors"
import { logError } from "../../lib/utils"

const chartConfig = {
  montos: {
    label: "Montos",
  },
  pendiente: {
    label: "Pendiente",
    color: getOrdenPagoColorValue('pendiente', 'main'),
  },
  procesando: {
    label: "Procesando",
    color: getOrdenPagoColorValue('procesando', 'main'),
  },
  pagado: {
    label: "Pagado",
    color: getOrdenPagoColorValue('pagado', 'main'),
  },
  expirado: {
    label: "Expirado",
    color: getOrdenPagoColorValue('expirado', 'main'),
  },
  cancelado: {
    label: "Cancelado",
    color: getOrdenPagoColorValue('cancelado', 'main'),
  },
}

// Componente personalizado para tooltip con formato CLP
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const date = new Date(label)
    const formattedDate = date.toLocaleDateString("es-CL", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[200px]">
        <p className="font-medium text-gray-900 mb-2">{formattedDate}</p>
        {payload
          .filter(entry => entry.value > 0)
          .map((entry, index) => (
            <div key={index} className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-sm" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm text-gray-700">{entry.name}:</span>
              </div>
              <span className="font-semibold text-gray-900 ml-3">
                CLP ${entry.value.toLocaleString('es-CL')}
              </span>
            </div>
          ))}
      </div>
    )
  }
  return null
}

export default function OrdenesPagoChart() {
  const [chartData, setChartData] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [activeChart, setActiveChart] = React.useState("pendiente")

  React.useEffect(() => {
    const loadOrdenesPagoData = async () => {
      try {
        setLoading(true)
        const ordenesPago = await OrdenespagoService.getOrdenesPago()
        
        // Agrupar órdenes por día y estado
        const groupedData = ordenesPago.reduce((acc, orden) => {
          const fecha = new Date(orden.created_at)
          const dateKey = fecha.toISOString().slice(0, 10) // YYYY-MM-DD
          
          if (!acc[dateKey]) {
            acc[dateKey] = {
              date: dateKey,
              pendiente: 0,
              procesando: 0,
              pagado: 0,
              expirado: 0,
              cancelado: 0
            }
          }
          
          const monto = parseFloat(orden.monto) || 0
          acc[dateKey][orden.estado] += monto
          
          return acc
        }, {})
        
        // Convertir a array y ordenar por fecha (últimos 30 días)
        const processedData = Object.values(groupedData)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(-30)
        
        setChartData(processedData)
      } catch (error) {
        logError("Error loading ordenes pago data:", error, { ignoreAbort: true })
      } finally {
        setLoading(false)
      }
    }

    loadOrdenesPagoData()
  }, [])

  const total = React.useMemo(
    () => ({
      pendiente: chartData.reduce((acc, curr) => acc + curr.pendiente, 0),
      procesando: chartData.reduce((acc, curr) => acc + curr.procesando, 0),
      pagado: chartData.reduce((acc, curr) => acc + curr.pagado, 0),
      expirado: chartData.reduce((acc, curr) => acc + curr.expirado, 0),
      cancelado: chartData.reduce((acc, curr) => acc + curr.cancelado, 0),
    }),
    [chartData]
  )

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Órdenes de Pago</CardTitle>
          <CardDescription>Cargando datos...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center items-center h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="py-0">
      <CardHeader className="flex flex-col items-stretch border-b !p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 pt-4 pb-3 sm:!py-0">
          <CardTitle>Órdenes de Pago CLP</CardTitle>
          <CardDescription>
            Montos por estado - Últimos 30 días
          </CardDescription>
        </div>
        <div className="flex">
          {["pendiente", "procesando", "pagado", "expirado", "cancelado"].map((key) => {
            const chart = key
            return (
              <button
                key={chart}
                data-active={activeChart === chart}
                className="data-[active=true]:bg-muted/50 relative z-30 flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l sm:border-t-0 sm:border-l sm:px-8 sm:py-6"
                onClick={() => setActiveChart(chart)}
              >
                <span className="text-muted-foreground text-xs">
                  {chartConfig[chart].label}
                </span>
                <span className="text-lg leading-none font-bold sm:text-3xl">
                  CLP ${total[key].toLocaleString('es-CL')}
                </span>
              </button>
            )
          })}
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:p-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <BarChart
            accessibilityLayer
            data={chartData}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value)
                return date.toLocaleDateString("es-CL", {
                  month: "short",
                  day: "numeric",
                })
              }}
            />
            <ChartTooltip content={<CustomTooltip />} />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar
              dataKey="pendiente"
              stackId="a"
              fill={getOrdenPagoColorValue('pendiente', 'main')}
              radius={[0, 0, 4, 4]}
            />
            <Bar
              dataKey="procesando"
              stackId="a"
              fill={getOrdenPagoColorValue('procesando', 'main')}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="pagado"
              stackId="a"
              fill={getOrdenPagoColorValue('pagado', 'main')}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="expirado"
              stackId="a"
              fill={getOrdenPagoColorValue('expirado', 'main')}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="cancelado"
              stackId="a"
              fill={getOrdenPagoColorValue('cancelado', 'main')}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
